'use client';
import { useLocalStorageState, useSessionStorageState } from 'ahooks';
import dynamic from 'next/dynamic';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy/hooks/use-retention-strategy';
import { BaseVideoPlayer } from '@/app/components/shared/VideoPlayer/BaseVideoPlayer';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { HOME_PAGE, isEnterpriseDistribution, ONE_DAY, ONE_WEEK, SITE } from '@/app/constants';
import {
  useDoubleFestivalCampaign,
  useFetch,
  useGlobalModal,
  useSignupRewardVerification,
  useUserNexus,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import {
  cn,
  getTodayDateString,
  isTimestampExceeded,
  periodOfValidity,
  Q4_LAUNCH_LAST_SHOWN_DATE_KEY,
} from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { couponsListMyCoupons } from './apis';
import {
  ALL_OPEN_SCREEN_DIALOGS,
  COUPON_EXPIRING_SHOWN_IDS_KEY,
  DIALOG_COUPON_EXPIRING,
  DIALOG_DOUBLE_FESTIVAL,
  DIALOG_FIRST_DISCOUNT,
  DIALOG_GOOGLE_ADS,
  DIALOG_MULTI_GRID_TRIAL,
  DIALOG_Q4_LAUNCH,
  DIALOG_RETENTION_EXPIRING,
  DIALOG_RETENTION_LAST_DAY,
  DIALOG_USE_CLAW_ONLINE,
  DIALOG_VIP_TRIAL,
  MAX_RETENTION_SCREEN_COUNT,
  NOTIFICATION_PRIORITIES,
  OPEN_SCREEN_DIALOG_SESSION,
  RETENTION_SCREEN_EXPIRING_COUNT_KEY,
  SIGNUP_REWARD_TASK_CENTER_KEY,
} from './constants';
import { useGoogleAds } from './hooks/use-google-ads';
import type { CreditV1Coupon, DialogKey, DialogMap } from './types';
import { isExpiringSoon, reportHomePopupClick } from './utils';

const DynamicDoubleFestivalDialog = dynamic(
  () => import('@/app/components/shared/DoubleFestivalCampaign').then((m) => ({ default: m.DoubleFestivalDialog })),
  { loading: () => null, ssr: false },
);

const DynamicQ4LaunchDialog = dynamic(() => import('./Q4LaunchDialog').then((m) => ({ default: m.Q4LaunchDialog })), {
  loading: () => null,
  ssr: false,
});

/** 按弹窗拆分 chunk，仅在需要展示时加载，减轻首屏 JS */
const DynamicFirstDiscountDialog = dynamic(
  () => import('./FirstDiscountDialog').then((m) => ({ default: m.FirstDiscountDialog })),
  { loading: () => null, ssr: false },
);

const DynamicGoogleAdsDialog = dynamic(
  () => import('./GoogleAdsDialog').then((m) => ({ default: m.GoogleAdsDialog })),
  { loading: () => null, ssr: false },
);

const DynamicUseClawDialog = dynamic(() => import('./UseClawDialog').then((m) => ({ default: m.UseClawDialog })), {
  loading: () => null,
  ssr: false,
});

const DynamicMultiGridTrialDialog = dynamic(
  () => import('./MultiGridTrialDialog').then((m) => ({ default: m.MultiGridTrialDialog })),
  { loading: () => null, ssr: false },
);

const DynamicCouponExpiringDialog = dynamic(
  () => import('./CouponExpiringDialog').then((m) => ({ default: m.CouponExpiringDialog })),
  { loading: () => null, ssr: false },
);

const DynamicVipTrialDialog = dynamic(() => import('./VipTrialDialog').then((m) => ({ default: m.VipTrialDialog })), {
  loading: () => null,
  ssr: false,
});

const DynamicRetentionOpenScreenDialog = dynamic(
  () =>
    import('@/app/components/shared/RetentionStrategy/components/RetentionOpenScreenDialog').then((m) => ({
      default: m.RetentionOpenScreenDialog,
    })),
  { loading: () => null, ssr: false },
);

/** window load 后再等一拍，再给 idle，避免与大图/LCP 争（单位 ms） */
const OPEN_SCREEN_POST_LOAD_PADDING_MS = 2800;
const OPEN_SCREEN_IDLE_CALLBACK_TIMEOUT_MS = 7000;
const OPEN_SCREEN_NO_IDLE_FALLBACK_MS = 4500;
/** 等 nexus / region 这两个异步条件的上限（自挂载起，单位 ms），超时后按当前已知条件选窗 */
const OPEN_SCREEN_PERSONALIZATION_WAIT_MS = 8000;
const MULTI_GRID_TRIAL_DIALOG_VALID_PERIOD: [string, string] = [
  // China time: 2026-05-29 17:00 to 2026-06-29 17:00.
  '2026-05-29T09:00:00Z',
  '2026-06-29T09:00:00Z',
];

export const OpenScreenDialog: FC = () => {
  const pathname = usePathname();
  const isDoubleFestivalActive = useDoubleFestivalCampaign();
  const { isSubOneTimeOffer } = useUserNexus();
  const { phase: retentionPhase } = useRetentionStrategy();
  const { open: openTaskCenterDrawer } = useGlobalModal('taskCenterDrawer');

  // 用于记录会话状态的 session 存储
  const [sessionDialogState, setSessionDialogState] = useSessionStorageState<{
    dialogLocked: boolean;
  }>(OPEN_SCREEN_DIALOG_SESSION, {
    defaultValue: { dialogLocked: false },
  });

  // 当前显示的弹窗（本地状态）
  const [currentDialogKey, setCurrentDialogKey] = useState<DialogKey | null>(null);
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  /** 仅在 window load +（固定缓冲）+ idle 之后才参与开屏候选，合并原先双重 ready 闸门 */
  const [isOpenScreenSchedulingReady, setIsOpenScreenSchedulingReady] = useState(false);
  /** 等 nexus / region 的时间已用尽：到点后不再等，避免接口异常时一个弹窗都不弹 */
  const [hasPersonalizationWaitElapsed, setHasPersonalizationWaitElapsed] = useState(false);

  const [hasShownDialogs, setHasShownDialogs] = useLocalStorageState<string[]>('HAS_SHOWN_DIALOGS', {
    defaultValue: [],
  });

  // 用于记录弹窗展示的时间戳（支持基于时间间隔的重复展示策略）
  const [dialogTimestamps, setDialogTimestamps] = useLocalStorageState<Record<string, number>>(
    'APP_DIALOG_TIMESTAMPS',
    {
      defaultValue: {},
    },
  );

  const [q4LaunchLastShownDate, setQ4LaunchLastShownDate] = useLocalStorageState<string | undefined>(
    Q4_LAUNCH_LAST_SHOWN_DATE_KEY,
  );

  // 记录首期折扣弹窗的次数
  const [firstDiscountDialogCount, setFirstDiscountDialogCount] = useLocalStorageState<number>(
    'FIRST_DISCOUNT_DIALOG_COUNT',
    {
      defaultValue: 0,
    },
  );

  // 记录挽留开屏弹窗展示次数
  const [retentionScreenCount, setRetentionScreenCount] = useLocalStorageState<number>(
    RETENTION_SCREEN_EXPIRING_COUNT_KEY,
    { defaultValue: 0 },
  );

  // 记录上次打开弹窗的时间
  const [lastOpenScreenDialogTime, setLastOpenScreenDialogTime] = useLocalStorageState<number | null>(
    'LAST_OPEN_SCREEN_DIALOG_TIME',
    { defaultValue: null },
  );

  // 检查是否在24小时限制内
  const isWithin24HourLimit = useMemo(
    () => (lastOpenScreenDialogTime ? Date.now() - lastOpenScreenDialogTime < ONE_DAY : false),
    [lastOpenScreenDialogTime],
  );

  const {
    userInfo,
    isUserInfoInitialized,
    isSubscriptionInitialized,
    isTeamSpace,
    countryLevel,
    region,
    isNexusInitialized,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    isSubscriptionInitialized: state.isSubscriptionInitialized,
    isTeamSpace: state.isTeamSpace,
    countryLevel: state.countryLevel,
    region: state.region,
    isNexusInitialized: state.isNexusInitialized,
  }));

  // 使用 Google Ads Session Hook
  const { shouldCheckGoogleAds, isEligible, markAdsShown, adsSessionStateVisited } = useGoogleAds({
    pathname,
    isUserInfoInitialized,
    userInfo,
    sessionDialogLocked: sessionDialogState?.dialogLocked ?? false,
  });

  const signupVerification = useSignupRewardVerification({
    userId:
      SITE === 'global' && !isEnterpriseDistribution && !isTeamSpace && isUserInfoInitialized
        ? userInfo?.id
        : undefined,
  });
  const signupTaskCenterIdentity =
    signupVerification.state?.registered_at && userInfo?.id
      ? `${SIGNUP_REWARD_TASK_CENTER_KEY}:${userInfo.id}:${signupVerification.state.registered_at}`
      : undefined;
  const signupTaskCenterEligible =
    signupVerification.state?.enabled &&
    !!signupTaskCenterIdentity &&
    signupVerification.state?.rewards.some(
      (reward) =>
        (reward.reward_type === 'new_user_credit' || reward.reward_type === 'invitecode_credit') &&
        ['pending_claim', 'pending_verification', 'ready_to_claim'].includes(reward.reward_status),
    ) &&
    !hasShownDialogs?.includes(signupTaskCenterIdentity);
  const openedSignupTaskCenterIdentity = useRef<string | undefined>(undefined);

  // 记录优惠券快到期弹窗已弹过的 coupon id 列表
  const [couponExpiringShownIds, setCouponExpiringShownIds] = useLocalStorageState<string[]>(
    COUPON_EXPIRING_SHOWN_IDS_KEY,
    { defaultValue: [] },
  );

  // 即将到期弹窗的优惠券
  const [expiringCoupon, setExpiringCoupon] = useState<CreditV1Coupon | null>(null);
  // 用 ref 读快照，避免 setCouponExpiringShownIds 触发本 effect 重跑、把刚展示的弹窗清空
  const couponExpiringShownIdsRef = useRef<string[]>([]);
  useEffect(() => {
    couponExpiringShownIdsRef.current = couponExpiringShownIds ?? [];
  }, [couponExpiringShownIds]);

  // 登录后拉取一次优惠券，供到期弹窗使用
  useFetch(() => couponsListMyCoupons({ query: { status: 'unused', 'pager.pagesz': 100 } }), {
    ready: isUserInfoInitialized && !!userInfo,
    refreshDeps: [userInfo?.id],
    onSuccess: ({ data }) => {
      const coupons = data?.coupons ?? [];
      // 即将到期：到期时间 ≤ 2 天，且尚未弹过。
      // 多张同时到期时只弹一张；选取规则（优惠力度大优先、力度相同最先到期优先）
      // 由后端排序保证——前端按返回顺序取第一个未弹过的，不要在此处加 sort 抢契约
      const shownIds = couponExpiringShownIdsRef.current;
      const expiring = coupons.find((c) => isExpiringSoon(c.expired_at) && c.id && !shownIds.includes(c.id));
      setExpiringCoupon(expiring ?? null);
    },
    // 拉券失败不阻断开屏流程，弹窗静默不展示
    onError: () => {},
  });

  // 获取所有符合条件的候选弹窗（按优先级排序）
  const getAvailableDialogs = (): DialogKey[] => {
    // 会话已锁定则不再显示任何弹窗
    if (sessionDialogState?.dialogLocked) {
      return [];
    }

    // 如果距离上次弹窗时间小于24小时，仅放行 Google Ads（时效性强，不受24h限制）
    const availableDialogKeys = isWithin24HourLimit ? [DIALOG_Q4_LAUNCH, DIALOG_GOOGLE_ADS] : ALL_OPEN_SCREEN_DIALOGS;

    const dialogs = availableDialogKeys
      .filter((key) => {
        if (!notificationSettings[key]?.showPreConditions) {
          return false;
        }
        if (key === DIALOG_FIRST_DISCOUNT) {
          // 首期折扣弹窗：若在记录中，则不显示
          if (hasShownDialogs?.includes(key)) {
            return false;
          }
          // 首期折扣弹窗：若不在记录中，则检查是否超过一周
          return isTimestampExceeded(dialogTimestamps?.[key], ONE_WEEK);
        }
        if (key === DIALOG_Q4_LAUNCH) {
          return q4LaunchLastShownDate !== getTodayDateString(currentTime ?? new Date());
        }
        if (key === DIALOG_GOOGLE_ADS) {
          // Google Ads弹窗：根据 Hook 判断
          return isEligible === true;
        }
        if (key === DIALOG_DOUBLE_FESTIVAL) {
          return isTimestampExceeded(dialogTimestamps?.[key], ONE_DAY);
        }
        if (key === DIALOG_USE_CLAW_ONLINE) {
          // Claw 弹窗：每天出现一次，基于时间戳判断是否超过一天
          return isTimestampExceeded(dialogTimestamps?.[key], ONE_DAY);
        }
        if (key === DIALOG_VIP_TRIAL) {
          // VIP 试用弹窗：每天出现一次
          return isTimestampExceeded(dialogTimestamps?.[key], ONE_DAY);
        }
        if (key === DIALOG_MULTI_GRID_TRIAL) {
          // 多宫格试用弹窗：活动期间每天出现一次，基于时间戳判断是否超过一天
          return isTimestampExceeded(dialogTimestamps?.[key], ONE_DAY);
        }
        if (key === DIALOG_RETENTION_EXPIRING) {
          return (
            (retentionScreenCount ?? 0) < MAX_RETENTION_SCREEN_COUNT &&
            isTimestampExceeded(dialogTimestamps?.[key], ONE_DAY)
          );
        }
        if (key === DIALOG_RETENTION_LAST_DAY) {
          return !hasShownDialogs?.includes(key);
        }
        // 其他弹窗按原逻辑
        return !hasShownDialogs?.includes(key);
      })
      .sort(
        // 按优先级排序，优先级高的在前
        (a, b) =>
          NOTIFICATION_PRIORITIES[b as keyof typeof NOTIFICATION_PRIORITIES] -
          NOTIFICATION_PRIORITIES[a as keyof typeof NOTIFICATION_PRIORITIES],
      );

    return dialogs;
  };

  useEffect(() => {
    setCurrentTime(new Date());
  }, []);

  useEffect(() => {
    let paddingId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;
    let fallbackId: ReturnType<typeof setTimeout> | undefined;

    const scheduleActivate = () => {
      const activate = () => setIsOpenScreenSchedulingReady(true);
      if ('requestIdleCallback' in globalThis && typeof globalThis.requestIdleCallback === 'function') {
        idleId = globalThis.requestIdleCallback(activate, { timeout: OPEN_SCREEN_IDLE_CALLBACK_TIMEOUT_MS });
      } else {
        fallbackId = globalThis.setTimeout(activate, OPEN_SCREEN_NO_IDLE_FALLBACK_MS);
      }
    };

    const onLoad = () => {
      paddingId = globalThis.setTimeout(scheduleActivate, OPEN_SCREEN_POST_LOAD_PADDING_MS);
    };

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }

    return () => {
      window.removeEventListener('load', onLoad);
      if (paddingId !== undefined) {
        globalThis.clearTimeout(paddingId);
      }
      if (idleId !== undefined && 'cancelIdleCallback' in globalThis) {
        globalThis.cancelIdleCallback(idleId);
      }
      if (fallbackId !== undefined) {
        globalThis.clearTimeout(fallbackId);
      }
    };
  }, []);

  // nexus / region 是并发拉的，选窗要等它们到齐才能正确比较优先级；
  // 但接口异常时不能无限等，这里给一个上限，到点后按已知条件继续选窗。
  useEffect(() => {
    const timerId = globalThis.setTimeout(
      () => setHasPersonalizationWaitElapsed(true),
      OPEN_SCREEN_PERSONALIZATION_WAIT_MS,
    );
    return () => globalThis.clearTimeout(timerId);
  }, []);

  useEffect(() => {
    // 如果24小时限制已过期且会话被锁定且没有当前弹窗且ads会话未被锁定，则重置主会话锁定
    if (!isWithin24HourLimit && sessionDialogState?.dialogLocked && !currentDialogKey && !adsSessionStateVisited) {
      setSessionDialogState({ dialogLocked: false });
    }
  }, [isWithin24HourLimit, sessionDialogState?.dialogLocked, currentDialogKey, adsSessionStateVisited]);

  const dismissDialog = () => {
    setCurrentDialogKey(null);
  };

  const notificationSettings: DialogMap = {
    [DIALOG_Q4_LAUNCH]: {
      type: 'component',
      component: <DynamicQ4LaunchDialog open={currentDialogKey === DIALOG_Q4_LAUNCH} onClose={dismissDialog} />,
      showPreConditions: true,
      priority: NOTIFICATION_PRIORITIES[DIALOG_Q4_LAUNCH],
    },
    [DIALOG_DOUBLE_FESTIVAL]: {
      type: 'component',
      component: (
        <DynamicDoubleFestivalDialog
          open={currentDialogKey === DIALOG_DOUBLE_FESTIVAL}
          onClose={dismissDialog}
          onNavigate={() => reportHomePopupClick(DIALOG_DOUBLE_FESTIVAL)}
        />
      ),
      showPreConditions: isDoubleFestivalActive,
      priority: NOTIFICATION_PRIORITIES[DIALOG_DOUBLE_FESTIVAL],
    },
    [DIALOG_FIRST_DISCOUNT]: {
      type: 'component',
      component: (
        <DynamicFirstDiscountDialog open={currentDialogKey === DIALOG_FIRST_DISCOUNT} onClose={dismissDialog} />
      ),
      showPreConditions: isSubOneTimeOffer,
      priority: NOTIFICATION_PRIORITIES[DIALOG_FIRST_DISCOUNT],
    },
    [DIALOG_GOOGLE_ADS]: {
      type: 'component',
      component: <DynamicGoogleAdsDialog open={currentDialogKey === DIALOG_GOOGLE_ADS} onClose={dismissDialog} />,
      showPreConditions: shouldCheckGoogleAds,
      priority: NOTIFICATION_PRIORITIES[DIALOG_GOOGLE_ADS],
    },
    [DIALOG_USE_CLAW_ONLINE]: {
      type: 'component',
      component: <DynamicUseClawDialog open={currentDialogKey === DIALOG_USE_CLAW_ONLINE} onClose={dismissDialog} />,
      // 等 GetMyClaws 拉完之后再判断：避免空窗期误把"尚未拉到"当成"没有 claw"。
      // 团队空间下不展示 claw 引导弹窗
      showPreConditions: !isTeamSpace,
      priority: NOTIFICATION_PRIORITIES[DIALOG_USE_CLAW_ONLINE],
    },
    [DIALOG_MULTI_GRID_TRIAL]: {
      type: 'component',
      component: (
        <DynamicMultiGridTrialDialog open={currentDialogKey === DIALOG_MULTI_GRID_TRIAL} onClose={dismissDialog} />
      ),
      showPreConditions:
        SITE === 'global' && !!currentTime && periodOfValidity(MULTI_GRID_TRIAL_DIALOG_VALID_PERIOD, currentTime),
      priority: NOTIFICATION_PRIORITIES[DIALOG_MULTI_GRID_TRIAL],
    },
    [DIALOG_RETENTION_EXPIRING]: {
      type: 'component',
      component: (
        <DynamicRetentionOpenScreenDialog
          open={currentDialogKey === DIALOG_RETENTION_EXPIRING}
          onClose={dismissDialog}
        />
      ),
      showPreConditions: retentionPhase === 'phase2',
      priority: NOTIFICATION_PRIORITIES[DIALOG_RETENTION_EXPIRING],
    },
    [DIALOG_RETENTION_LAST_DAY]: {
      type: 'component',
      component: (
        <DynamicRetentionOpenScreenDialog
          open={currentDialogKey === DIALOG_RETENTION_LAST_DAY}
          onClose={dismissDialog}
        />
      ),
      showPreConditions: retentionPhase === 'phase3',
      priority: NOTIFICATION_PRIORITIES[DIALOG_RETENTION_LAST_DAY],
    },
    [DIALOG_VIP_TRIAL]: {
      type: 'component',
      component: <DynamicVipTrialDialog open={currentDialogKey === DIALOG_VIP_TRIAL} onClose={dismissDialog} />,
      showPreConditions:
        SITE === 'global' &&
        !isTeamSpace &&
        pathname.includes('/home/recommend') &&
        userInfo?.subs_plan === 'free' &&
        isSubOneTimeOffer &&
        countryLevel !== 't1',
      priority: NOTIFICATION_PRIORITIES[DIALOG_VIP_TRIAL],
    },
    [DIALOG_COUPON_EXPIRING]: {
      type: 'component',
      component: expiringCoupon ? (
        <DynamicCouponExpiringDialog
          open={currentDialogKey === DIALOG_COUPON_EXPIRING}
          coupon={expiringCoupon}
          onClose={() => {
            // 关闭时清空 expiringCoupon，使 showPreConditions 变 false，
            // 避免重置会话锁后选窗 effect 重跑又把本弹窗重新选中（关不掉）
            setExpiringCoupon(null);
            dismissDialog();
          }}
        />
      ) : null,
      showPreConditions: !!expiringCoupon,
      priority: NOTIFICATION_PRIORITIES[DIALOG_COUPON_EXPIRING],
    },
  };

  // 统一处理：等待所有条件准备好后，选择优先级最高的弹窗显示
  useEffect(() => {
    // 所有开屏弹窗只在首页出现：非首页直接不参与选窗（统一门控，避免逐个弹窗漏写）
    if (!pathname.includes(HOME_PAGE)) return;
    // window load → 缓冲 → idle（或兜底）：未放行前不参与开屏候选
    if (!isOpenScreenSchedulingReady) return;
    // 等待用户信息初始化完成
    if (!isUserInfoInitialized) return;
    // 如果是登录状态，则等 subscriptions 的初始化完成后再显示弹窗
    if (userInfo && !isSubscriptionInitialized) return;
    // nexus（优惠资格）与 region 决定 VIP_TRIAL 等弹窗的 showPreConditions，
    // 它们与 subscriptions 并发拉取。不等它们就选窗会出现"谁先返回谁赢"：
    // 选中后立即锁会话且不再重选，同一账号同样操作可能弹到优先级更低的弹窗。
    // 超过等待上限则不再阻塞，避免接口异常时一个弹窗都不弹。
    if (!hasPersonalizationWaitElapsed && userInfo && (!isNexusInitialized || region === null)) return;

    if (!hasPersonalizationWaitElapsed && signupVerification.loading) return;

    // Registration qualification is account-specific and takes precedence over
    // marketing frequency. Reuse the old identity so this opens only once.
    if (signupTaskCenterEligible && signupTaskCenterIdentity) {
      if (openedSignupTaskCenterIdentity.current === signupTaskCenterIdentity) return;
      openedSignupTaskCenterIdentity.current = signupTaskCenterIdentity;
      setHasShownDialogs((prev) => [...(prev || []), signupTaskCenterIdentity]);
      setSessionDialogState({ dialogLocked: true });
      setLastOpenScreenDialogTime(Date.now());
      setCurrentDialogKey(null);
      openTaskCenterDrawer({ fromPage: 'home_task_entry' });
      return;
    }
    // 如果已经有弹窗显示，不再选择新的弹窗
    if (currentDialogKey) return;

    // 获取所有可用的候选弹窗
    const availableDialogs = getAvailableDialogs();
    if (availableDialogs.length === 0) return;

    // 找到优先级最高且满足显示条件的弹窗
    const firstDialog = availableDialogs[0];
    if (firstDialog) {
      // 立即锁定会话并设置当前弹窗
      setSessionDialogState({ dialogLocked: true });
      setCurrentDialogKey(firstDialog);
    }
  }, [
    userInfo,
    currentDialogKey,
    sessionDialogState?.dialogLocked,
    isUserInfoInitialized,
    isSubscriptionInitialized,
    pathname,
    isSubOneTimeOffer,
    shouldCheckGoogleAds,
    isEligible,
    retentionPhase,
    isOpenScreenSchedulingReady,
    expiringCoupon,
    region,
    isTeamSpace,
    isNexusInitialized,
    hasPersonalizationWaitElapsed,
    isDoubleFestivalActive,
    q4LaunchLastShownDate,
    signupTaskCenterEligible,
    signupTaskCenterIdentity,
    openTaskCenterDrawer,
    signupVerification.loading,
    currentTime,
  ]);

  // 展示弹窗时，记录弹窗已显示
  useEffect(() => {
    if (!currentDialogKey) return;
    const currentDialogContent = notificationSettings[currentDialogKey as DialogKey];
    if (!currentDialogContent) return;

    // 根据弹窗类型采用不同的记录策略
    if (currentDialogKey === DIALOG_Q4_LAUNCH) {
      setQ4LaunchLastShownDate(getTodayDateString());
    } else if (currentDialogKey === DIALOG_FIRST_DISCOUNT) {
      // 首期折扣弹窗：最多显示5次，超过5次后，永久记录，不再显示
      // 当前记录了四次展示，第五次展示时不再计数，而是设置 hasShownDialogs 弹窗记录，并删除次数记录
      if (firstDiscountDialogCount >= 4) {
        setHasShownDialogs((prev) => [...(prev || []), currentDialogKey]);
        safeStorage.removeItem('FIRST_DISCOUNT_DIALOG_COUNT');
      } else {
        setFirstDiscountDialogCount((prev) => (prev ?? 0) + 1);
        // 首期折扣弹窗：记录时间戳，一周后可再次显示
        setDialogTimestamps((prev) => ({
          ...prev,
          [currentDialogKey]: Date.now(),
        }));
      }
    } else if (currentDialogKey === DIALOG_GOOGLE_ADS) {
      // Google Ads弹窗：使用 Hook 提供的方法
      markAdsShown();
      // Google Ads 不更新 24h 时间戳
      currentDialogContent.openPreCallback?.();
      return;
    } else if (currentDialogKey === DIALOG_RETENTION_EXPIRING) {
      setRetentionScreenCount((prev) => (prev ?? 0) + 1);
      setDialogTimestamps((prev) => ({
        ...prev,
        [currentDialogKey]: Date.now(),
      }));
    } else if (currentDialogKey === DIALOG_RETENTION_LAST_DAY) {
      setHasShownDialogs((prev) => [...(prev || []), currentDialogKey]);
    } else if (currentDialogKey === DIALOG_COUPON_EXPIRING) {
      // 优惠券快到期弹窗：按 coupon id 记录，每张券弹一次
      if (expiringCoupon?.id) {
        setCouponExpiringShownIds((prev) => [...(prev || []), expiringCoupon.id as string]);
      }
    } else if (
      currentDialogKey === DIALOG_USE_CLAW_ONLINE ||
      currentDialogKey === DIALOG_MULTI_GRID_TRIAL ||
      currentDialogKey === DIALOG_DOUBLE_FESTIVAL ||
      currentDialogKey === DIALOG_VIP_TRIAL
    ) {
      setDialogTimestamps((prev) => ({
        ...prev,
        [currentDialogKey]: Date.now(),
      }));
    } else {
      // 其他弹窗按原逻辑永久记录
      setHasShownDialogs((prev) => [...(prev || []), currentDialogKey]);
    }

    // 记录弹窗时间（Google Ads 例外，不更新 24h 时间戳避免影响其他弹窗）
    if (currentDialogKey !== DIALOG_GOOGLE_ADS) {
      setLastOpenScreenDialogTime(Date.now());
    }
    currentDialogContent.openPreCallback?.();
  }, [currentDialogKey, firstDiscountDialogCount, expiringCoupon]);

  const renderDialogComponent = (dialogKey: DialogKey) => {
    const currentDialog = notificationSettings[dialogKey as DialogKey];
    if (!currentDialog) return null;
    return (
      <Dialog open={!!dialogKey} onOpenChange={dismissDialog}>
        {currentDialog.type === 'component' ? (
          currentDialog.component
        ) : (
          <DialogContent
            className={cn(
              'gap-0 overflow-hidden border-none p-0 pb-24 md:p-0',
              currentDialog?.classNames?.contentClassName,
            )}
            closable={false}
            disableOverlayClose={currentDialog?.disableOverlayClose}
          >
            <DialogHeader
              className={cn(
                'relative h-fit gap-0 overflow-y-auto pb-[88px] sm:gap-4 md:pb-[84px]',
                currentDialog?.classNames?.headerClassName,
              )}
            >
              <DialogClose
                className={cn(
                  'fixed top-4 right-4 z-50 cursor-pointer text-white text-xl',
                  currentDialog?.classNames?.closeClassName,
                )}
              />
              {currentDialog?.mediaType === 'video' ? (
                <BaseVideoPlayer
                  src={currentDialog?.media ?? ''}
                  poster={currentDialog.poster}
                  className={cn('w-full', currentDialog?.classNames?.mediaClassName)}
                  autoPlay={true}
                  hasAudio={true}
                  enableSpaceShortcut
                />
              ) : (
                <img
                  src={currentDialog?.media}
                  alt="new-features-header"
                  className={cn('aspect-[960/396] w-full', currentDialog?.classNames?.mediaClassName)}
                />
              )}
              <DialogTitle
                className={cn(
                  'px-6 font-semibold text-2xl leading-8 sm:text-2xl md:px-4',
                  currentDialog?.classNames?.titleClassName,
                )}
              >
                {currentDialog?.dialogTitle}
              </DialogTitle>
              <div className="px-6 text-sm text-system-white leading-6 md:px-4">{currentDialog?.dialogContent}</div>
            </DialogHeader>
            <div className="absolute bottom-0 left-0 flex w-full justify-center bg-[linear-gradient(180deg,rgba(25,29,37,0.00)0%,#191D25_76.04%)] px-4 pt-5 pb-6 sm:pb-4">
              <Button
                variant="primary"
                size="lg"
                className={cn('h-11 px-20 py-4 text-sm', currentDialog?.classNames?.buttonClassName)}
                onClick={currentDialog?.confirmCallback || dismissDialog}
              >
                {currentDialog?.buttonChild}
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    );
  };

  return currentDialogKey ? renderDialogComponent(currentDialogKey as DialogKey) : null;
};
